import { useCallback, useEffect, useState } from 'react';
import {
  ButtonV2,
  ConditionalRender,
  EditIcon,
  MFASetup,
  Modal,
  TextInput,
  Tooltip,
} from '@medeloop/medeloop-components-library';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { UpdateUserDTO, verifyUserAttribute, generateVerificationCode } from 'src/api/users';
import { showToast } from 'src/store/toast';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { profileEditorStyles } from './styles';
import { useGetUserById, useUpdateUserMutation } from 'src/api/users-queries';
import { SkeletonLine } from '../skeleton-line/skeleton-line';
import { UpdatePasswordModal } from '../update-password-modal/update-password-modal';
import { UpdateEmailModal } from '../update-email-modal/update-email-modal';
import { UpdatePhoneNumberModal } from '../update-phone-number-modal/update-phone-number-modal';
import { configuration } from 'src/configuration';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PreferredMfaSetting } from 'src/types/users';

const DEBOUNCE_AUTO_SAVE_TIME = 2000; // 2 seconds
const DISABLED_FIELD_STYLES = 'cursor-not-allowed [&>div>div]:!bg-gray-800 [&>div>div]:opacity-60 [&>div>div>div>input]:cursor-not-allowed';

interface ProfileFieldInputProps {
  field: {
    label: string;
    name: keyof UpdateUserDTO;
    placeholder: string;
    readOnly?: boolean;
  };
  value: string;
  isLoading: boolean;
  onUpdate: (field: string, value: string) => void;
  onClick: (field: string) => void;
}

const ProfileFieldInput = ({ field, value, isLoading, onUpdate, onClick }: ProfileFieldInputProps) => {
  const { t } = useTranslation();
  const isReadOnly = field.readOnly;

  const inputElement = (
    <div className={isReadOnly ? DISABLED_FIELD_STYLES : ''}>
      <TextInput
        placeholder={field.placeholder}
        value={value}
        onChange={isReadOnly ? undefined : (e) => onUpdate(field.name, e.target.value)}
        type="text"
        onClick={isReadOnly ? undefined : () => onClick(field.name)}
        disabled={isReadOnly}
      />
    </div>
  );

  const wrappedInput = isReadOnly ? (
    <Tooltip delayDuration={100}>
      <Tooltip.Trigger asChild>
        <div>{inputElement}</div>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="top">
          <p>{t('to_change_use_sso_provider', { field: field.label })}</p>
          <Tooltip.Arrow />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  ) : (
    inputElement
  );

  return (
    <div className={profileEditorStyles.item({ withInput: true })}>
      <span>{field.label}</span>
      <span className={profileEditorStyles.input()}>
        <ConditionalRender
          assertion={!isLoading}
          fallback={
            <div className="flex items-center w-72 h-14">
              <SkeletonLine />
            </div>
          }
        >
          {wrappedInput}
        </ConditionalRender>
      </span>
    </div>
  );
};

export const ProfileEditor = () => {
  const token = useSelector(selectAuthToken);
  const { mfaSetup: twoFactorSetupEnabled } = useFlags();
  const { accessToken, userId, isSSO, idToken } = useSelector(selectAccount);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { accountApiUrl } = configuration;

  const [userProfile, setUserProfile] = useState<UpdateUserDTO | null>(null);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isUpdatePasswordModalOpen, setIsUpdatePasswordModalOpen] = useState(false);
  const [isUpdateEmailModalOpen, setIsUpdateEmailModalOpen] = useState(false);
  const [isUpdatePhoneNumberModalOpen, setIsUpdatePhoneNumberModalOpen] = useState(false);
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [verificationAttribute, setVerificationAttribute] = useState('');

  const PROFILE_FIELDS: Array<{
    label: string;
    name: keyof UpdateUserDTO;
    placeholder: string;
    readOnly?: boolean;
    visible: boolean;
  }> = [
    {
      label: t('first_name_header'),
      name: 'givenName',
      placeholder: 'Write your first name',
      visible: true,
    },
    {
      label: t('last_name_header'),
      name: 'familyName',
      placeholder: 'Write your last name',
      visible: true,
    },
    {
      label: t('email_header'),
      name: 'email',
      placeholder: 'Write your email',
      readOnly: isSSO,
      visible: true,
    },
    {
      label: t('phone_number_header'),
      name: 'phoneNumber',
      placeholder: 'Write your phone number',
      readOnly: isSSO,
      visible: !twoFactorSetupEnabled,
    },
  ];

  const userDetailsQuery = useGetUserById(userId);

  const { preferredMfaSetting, phoneNumber } = userDetailsQuery.data ?? {};

  const updateUserMutation = useUpdateUserMutation({ showConfirmationToast: true });

  const generateVerificationCodeMutation = useMutation({
    mutationFn: ({
      accessToken,
      attributeName,
    }: {
      accessToken: string;
      attributeName: string;
    }) => {
      return generateVerificationCode({ token, accessToken, attributeName });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('verification_sent', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
    },
  });

  const verifyUserAttributeMutation = useMutation({
    mutationFn: ({
      attributeName,
      code,
      accessToken,
    }: {
      attributeName: string;
      code: string;
      accessToken: string;
    }) => {
      return verifyUserAttribute({ token, attributeName, code, accessToken });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('email_phone_verified', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          }),
        }),
      );
      userDetailsQuery.refetch();
    },
  });

  useEffect(() => {
    if (userDetailsQuery.data) {
      setUserProfile({
        userId,
        email: userDetailsQuery.data.email,
        givenName: userDetailsQuery.data.givenName,
        familyName: userDetailsQuery.data.familyName,
        phoneNumber: userDetailsQuery.data.phoneNumber,
      });
    }
  }, [userDetailsQuery.data, userId]);

  const autoSaveDebounced = useCallback(
    debounce((userProfileDebounce: UpdateUserDTO, field: string) => {
      const updateData: UpdateUserDTO = {
        userId,
        email: isSSO ? '' : userProfileDebounce?.email || '',
        givenName: userProfileDebounce?.givenName || '',
        familyName: userProfileDebounce?.familyName || '',
        phoneNumber: isSSO ? '' : userProfileDebounce?.phoneNumber || '',
      };

      updateUserMutation.mutate(updateData);

      if (!isSSO && field === 'email' && userProfileDebounce.email !== userProfile?.email) {
        setIsVerificationModalOpen(true);
        setVerificationAttribute('email');
      }

      if (
        !isSSO &&
        field === 'phoneNumber' &&
        userProfileDebounce.phoneNumber !== userProfile?.phoneNumber
      ) {
        setIsVerificationModalOpen(true);
        setVerificationAttribute('phone_number');
      }
    }, DEBOUNCE_AUTO_SAVE_TIME),
    [isSSO, updateUserMutation, userProfile],
  );

  const handleUpdateUser = (field: string, data: string) => {
    setUserProfile((prev) => ({
      userId,
      email: prev?.email || '',
      phoneNumber: prev?.phoneNumber || '',
      givenName: prev?.givenName || '',
      familyName: prev?.familyName || '',
      [field]: data,
    }));

    if ((field === 'phoneNumber' || field === 'email') && data === '') {
      return;
    }

    autoSaveDebounced(
      {
        userId,
        email: userProfile?.email || '',
        phoneNumber: userProfile?.phoneNumber || '',
        givenName: userProfile?.givenName || '',
        familyName: userProfile?.familyName || '',
        [field]: data,
      },
      field,
    );
  };

  const handleVerify = () => {
    verifyUserAttributeMutation.mutate({
      accessToken,
      attributeName: verificationAttribute,
      code: verificationCode,
    });
    setIsVerificationModalOpen(false);
  };

  const handleGenerateVerificationCode = () => {
    generateVerificationCodeMutation.mutate({
      accessToken,
      attributeName: verificationAttribute,
    });
  };

  const handleTextInputClick = (field: string) => {
    if (!isSSO && field === 'email') {
      setIsUpdateEmailModalOpen(true);
    } else if (!isSSO && field === 'phoneNumber') {
      setIsUpdatePhoneNumberModalOpen(true);
    }
  };

  const handleTwoFactorModalCancel = () => {
    setIsTwoFactorModalOpen(false);
  };

  const handleTwoFactorModalError = (error: string) => {
    dispatch(
      showToast({
        type: 'dangerous',
        message: error,
      }),
    );
  };

  const handleTwoFactorModalSetupCompleted = (selectedMethod: string) => {
    userDetailsQuery.refetch();

    if (
      selectedMethod !== PreferredMfaSetting.SOFTWARE_TOKEN_MFA &&
      selectedMethod !== PreferredMfaSetting.SMS_MFA
    ) {
      setIsTwoFactorModalOpen(false);
      return;
    }

    const selectedMethodDisplay =
      selectedMethod === PreferredMfaSetting.SOFTWARE_TOKEN_MFA ? 'Authenticator App' : `SMS`;

    dispatch(
      showToast({
        type: 'success',
        message: `Two-Factor Authentication setup completed with ${selectedMethodDisplay}`,
      }),
    );
    setIsTwoFactorModalOpen(false);
  };

  return (
    <>
      {userDetailsQuery.isError && (
        <div className={profileEditorStyles.error()}>{t('error_loading_profile')}</div>
      )}

      {updateUserMutation.isError && (
        <div className={profileEditorStyles.error()}>{t('error_editing_profile')}</div>
      )}

      <div className={profileEditorStyles.container()}>
        <div className={profileEditorStyles.item({ withInput: false })}>
          <span>Date created</span>
          <span className="text-content-on-dark-subdued">
            <ConditionalRender
              assertion={!userDetailsQuery.isLoading}
              fallback={
                <div className="w-72">
                  <SkeletonLine />
                </div>
              }
            >
              {userDetailsQuery.data?.createdAt &&
                new Date(userDetailsQuery.data.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
            </ConditionalRender>
          </span>
        </div>
        <div className={profileEditorStyles.item({ withInput: false })}>
          <span>User Groups</span>
          <span className="text-content-on-dark-subdued">
            <ConditionalRender
              assertion={!userDetailsQuery.isLoading}
              fallback={
                <div className="w-72">
                  <SkeletonLine />
                </div>
              }
            >
              {userDetailsQuery.data?.groups.map(
                (group, i) => `${group}${i < userDetailsQuery.data?.groups.length - 1 ? ', ' : ''}`,
              )}
            </ConditionalRender>
          </span>
        </div>
        {PROFILE_FIELDS.filter((field) => field.visible).map((field) => (
          <ProfileFieldInput
            key={field.name}
            field={field}
            value={userProfile?.[field.name] ?? ''}
            isLoading={userDetailsQuery.isLoading}
            onUpdate={handleUpdateUser}
            onClick={handleTextInputClick}
          />
        ))}

        {!isSSO && (
          <>
            {twoFactorSetupEnabled ? (
              <div className={profileEditorStyles.item({ withInput: false })}>
                <div className="flex flex-col">
                  <span>Two-Factor Authentication</span>
                  <span className="text-sm text-content-on-dark-subdued">
                    {preferredMfaSetting === PreferredMfaSetting.SOFTWARE_TOKEN_MFA
                      ? 'Authenticator App'
                      : preferredMfaSetting === PreferredMfaSetting.SMS_MFA
                        ? `SMS ${'\u00B7'} ${phoneNumber}`
                        : ''}
                  </span>
                </div>

                <span>
                  <ConditionalRender
                    assertion={!userDetailsQuery.isLoading}
                    fallback={
                      <div className="w-48 h-10">
                        <SkeletonLine />
                      </div>
                    }
                  >
                    <ButtonV2
                      startIcon={<EditIcon />}
                      variant="secondary"
                      className="py-2 px-5 rounded-full border border-cool-gray-600 text-sm"
                      onClick={() => setIsTwoFactorModalOpen(true)}
                    >
                      {t('edit')}
                    </ButtonV2>
                  </ConditionalRender>
                </span>
              </div>
            ) : null}
            <div className={profileEditorStyles.item({ withInput: false })}>
              <span>Password</span>
              <span>
                <ConditionalRender
                  assertion={!userDetailsQuery.isLoading}
                  fallback={
                    <div className="w-48 h-10">
                      <SkeletonLine />
                    </div>
                  }
                >
                  <ButtonV2
                    variant="secondary"
                    className="py-2 px-5 rounded-full border border-cool-gray-600 text-sm"
                    onClick={() => setIsUpdatePasswordModalOpen(true)}
                  >
                    {t('change_password')}
                  </ButtonV2>
                </ConditionalRender>
              </span>
            </div>
          </>
        )}

        {!isSSO && (
          <UpdatePasswordModal
            isModalOpen={isUpdatePasswordModalOpen}
            setIsModalOpen={setIsUpdatePasswordModalOpen}
          />
        )}
        {userProfile ? (
          <>
            {!isSSO && (
              <>
                <UpdateEmailModal
                  isModalOpen={isUpdateEmailModalOpen}
                  setIsModalOpen={setIsUpdateEmailModalOpen}
                  userProfile={userProfile}
                />
                <UpdatePhoneNumberModal
                  isModalOpen={isUpdatePhoneNumberModalOpen}
                  setIsModalOpen={setIsUpdatePhoneNumberModalOpen}
                  userProfile={userProfile}
                />
              </>
            )}
          </>
        ) : null}

        {isTwoFactorModalOpen && (
          <Modal
            title=""
            isOpen={isTwoFactorModalOpen}
            actionType="primary"
            onCancel={() => setIsTwoFactorModalOpen(false)}
            contentClassName="bg-purple-gray-900 !pt-4 pb-0 pl-0 pr-4 !rounded-2xl"
          >
            <MFASetup
              userId={userId}
              accessToken={accessToken}
              idToken={idToken}
              accountAPIUrl={accountApiUrl}
              onCancel={handleTwoFactorModalCancel}
              onError={handleTwoFactorModalError}
              onSetupCompleted={handleTwoFactorModalSetupCompleted}
              className="border-none -mt-4 pt-0"
            />
          </Modal>
        )}

        <Modal
          title={t('verify_your', {
            attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
          })}
          isOpen={isVerificationModalOpen}
          actionType="primary"
          onCancel={() => setIsVerificationModalOpen(false)}
          actionText={t('verify_header')}
          cancelText={t('cancel')}
          actionFunction={handleVerify}
        >
          <TextInput
            label={t('please_enter_verification_sent', {
              attribute: verificationAttribute === 'email' ? t('email') : t('phone_number'),
            })}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder={t('enter_verification_code')}
          />
          <span
            className="text-sm text-cyan-300 text-border-300 cursor-pointer"
            onClick={handleGenerateVerificationCode}
          >
            {t('resend_verification_code')}
          </span>
        </Modal>
      </div>
    </>
  );
};
